import { Empty, Pagination, Spin, Table as AntTable, Tag } from 'antd';
import { AxiosError } from 'axios';
import React, { useState } from 'react';

import { PermissionUsageMR, permissionsService } from '../../../services/permission';
import { openSuccessNotification } from '../../../utils/notifications';
import { showApiErrors } from '../../../utils/showApiErrors';
import { AddOrEditModal } from './AddOrEditModal';
import { TableActions } from './TableAction';

type TableProps = {
  permissions: PermissionUsageMR[];
  loadingTable: boolean;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onChanged: () => void;
  onPaginationChange: (value: number) => void;
};

const attributeNames = (permission: PermissionUsageMR): string => {
  const names = permission.params?.attributes?.map((attribute) => attribute.name) ?? [];

  return names.length ? names.join(', ') : '-';
};

const deleteDisabledReason = (permission: PermissionUsageMR): string | undefined => {
  if (permission.isProtected) {
    return 'Required by IAM itself - cannot be deleted';
  }

  if (permission.roleCount > 0) {
    return `Used by ${permission.roleCount} role(s) - remove it from them first`;
  }

  return undefined;
};

export const Table = ({
  permissions,
  loadingTable,
  totalItems,
  currentPage,
  pageSize,
  onChanged,
  onPaginationChange
}: TableProps): JSX.Element => {
  const [currEditIndex, setCurrEditIndex] = useState(-1);
  const [editModalVisible, setEditModalVisible] = useState(false);

  const closeEditModal = () => {
    setCurrEditIndex(-1);
    setEditModalVisible(false);
  };

  const openEditModal = (index: number) => {
    setCurrEditIndex(index);
    setEditModalVisible(true);
  };

  const handleDelete = async (permission: PermissionUsageMR) => {
    try {
      await permissionsService.remove(permission.id);
      openSuccessNotification({ message: 'Permission successfully deleted!' });
      onChanged();
    } catch (e) {
      showApiErrors(e as AxiosError);
    }
  };

  const columns = [
    {
      title: 'ID #',
      dataIndex: 'id',
      width: 90
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name: string, permission: PermissionUsageMR) => (
        <span>
          {name} {permission.isProtected && <Tag color="gold">protected</Tag>}
        </span>
      )
    },
    {
      title: 'Platform',
      dataIndex: 'platform'
    },
    {
      title: 'Attributes',
      key: 'attributes',
      render: (_: any, permission: PermissionUsageMR) => attributeNames(permission)
    },
    {
      title: 'Roles',
      dataIndex: 'roleCount',
      width: 100
    },
    {
      title: 'Actions',
      width: 150,
      key: 'operation',
      align: 'center' as const,
      render: (_: any, permission: PermissionUsageMR, index: number) => {
        return (
          <TableActions
            onEditClick={() => openEditModal(index)}
            onDeleteConfirm={() => void handleDelete(permission)}
            deleteDisabledReason={deleteDisabledReason(permission)}
          />
        );
      }
    }
  ];

  const pagination = (
    <Pagination
      className="ant-table-pagination ant-table-pagination-right"
      total={totalItems}
      current={currentPage}
      onChange={onPaginationChange}
      showSizeChanger={false}
      pageSize={pageSize}
    />
  );

  return (
    <div>
      <div className="hidden md:block">
        <AntTable
          style={{ marginTop: '3%' }}
          loading={loadingTable}
          rowKey={({ id }) => id}
          dataSource={permissions}
          columns={columns}
          pagination={false}
          scroll={{ x: 1200, scrollToFirstRowOnChange: true }}
          expandRowByClick={true}
          onRow={(_, index) => ({
            onClick: () => openEditModal(index!)
          })}
          footer={() => pagination}
        />
      </div>

      <div className="block md:hidden">
        {loadingTable ? (
          <div className="flex justify-center py-10">
            <Spin />
          </div>
        ) : permissions.length === 0 ? (
          <div className="rounded-lg border border-grey-border bg-surface py-10">
            <Empty />
          </div>
        ) : (
          <ul className="mt-4 space-y-3">
            {permissions.map((permission, index) => (
              <li
                key={permission.id}
                role="button"
                tabIndex={0}
                onClick={() => openEditModal(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openEditModal(index);
                  }
                }}
                className="cursor-pointer rounded-lg border border-grey-border bg-surface p-4 shadow-sm transition-colors active:bg-grey-bg"
              >
                <div className="min-w-0">
                  <div className="truncate text-base font-semibold text-grey-text">
                    {permission.name} {permission.isProtected && <Tag color="gold">protected</Tag>}
                  </div>
                  <div className="mt-0.5 text-xs text-grey-secondary">ID #{permission.id}</div>
                </div>

                <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1.5 text-sm">
                  <dt className="text-grey-secondary">Platform</dt>
                  <dd className="truncate text-grey-text">{permission.platform}</dd>
                  <dt className="text-grey-secondary">Attributes</dt>
                  <dd className="truncate text-grey-text">{attributeNames(permission)}</dd>
                  <dt className="text-grey-secondary">Roles</dt>
                  <dd className="truncate text-grey-text">{permission.roleCount}</dd>
                </dl>

                <div className="mt-3 flex justify-end border-t border-grey-border pt-3">
                  <TableActions
                    onEditClick={() => openEditModal(index)}
                    onDeleteConfirm={() => void handleDelete(permission)}
                    deleteDisabledReason={deleteDisabledReason(permission)}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex justify-end">{pagination}</div>
      </div>

      {currEditIndex !== -1 && (
        <AddOrEditModal
          modalVisible={editModalVisible}
          permission={permissions[currEditIndex]}
          onCancel={closeEditModal}
          onSuccess={() => {
            closeEditModal();
            onChanged();
          }}
        />
      )}
    </div>
  );
};
